import dayjs from 'dayjs';
import React from 'react';
import styled from 'styled-components';

import { BlockedFlags, getFlagFromCardStatus } from '../..';
import { closeModal, initModal } from '../../../components/modal';
import {
  IdentificationListProps,
  IdentificationList,
  Card as CardForSharedDetails,
  SharedDetails
} from '../../details-modal';
import { useCardQuery, BlockHistory as BlockHistoryFromQuery, CardIdentifier } from '../..';
import Button from '../../../components/button';
import { CardQueryErrorComponent } from '../..';
import { Cross } from '../../../assets/icons';
import { getDeviceInfo } from '../../../lib/device-info';
import { getLocalDateTimeTZ } from '../../../lib/time';
import LoadingSpinner from '../../../components/loading-spinner';
import { Text } from '../../../components/typography';

export const showBlocked = (cardIdentifier: CardIdentifier) =>
  initModal({
    actions: [],
    content: <ModalContentWithQuery {...cardIdentifier} />,
    open: true,
    title: 'Rewards Card Blocked',
    width: 'default'
  });

function ModalContentWithQuery(cardIdentifier: CardIdentifier) {
  const { data, error, loading } = useCardQuery(cardIdentifier);

  if (loading) {
    return (
      <p className="qa-player-card-rewards-card-blocked-loading">
        <LoadingSpinner />
      </p>
    );
  } else if (error || !data || !data.card) {
    return <CardQueryErrorComponent close={closeModal} />;
  } else {
    return <ModalContent {...data.card} />;
  }
}

export type Card = CardForSharedDetails & {
  player?: BlockedFlags & IdentificationListProps & { blockHistoryList: BlockHistory[] };
};

export function ModalContent(card: Card) {
  return (
    <RewardsModal className="qa-player-card-rewards-card-blocked-modal">
      <Cross />
      <p className="centered">
        <strong>This rewards card is blocked from use for the following reason(s):</strong>
      </p>
      <hr />
      <SharedDetails {...card} />
      <p>
        <span>Status</span>
        <span className="qa-player-card-rewards-card-blocked-status">
          <Activity size="xs" weight="regular">
            {card.player ? getFlagFromCardStatus(card.player) : 'Blocked'}
          </Activity>
        </span>
      </p>
      <hr />
      {card.player && (
        <>
          <IdentificationList identifications={card.player.identifications} />
          {card.player.blockHistoryList.map(BlockDetails)}
        </>
      )}
      <p className="centered">Call Customer Support Admin if you need further assistance.</p>
      <CloseButton
        className="qa-player-card-rewards-card-details-blocked-close-button"
        modifiers="secondaryRound"
        onClick={closeModal}>
        Close
      </CloseButton>
    </RewardsModal>
  );
}

type BlockHistory = Pick<BlockHistoryFromQuery, 'blockComment' | 'blockLabel' | 'blockStartDate' | 'blockType'>;

const BlockDetails = ({ blockComment, blockLabel, blockStartDate, blockType }: BlockHistory, index: number) => (
  <div className="qa-player-card-rewards-card-block" key={`qa-player-card-rewards-card-block-${index}`}>
    <p>
      <span>{blockLabel}</span>
      <span>
        {dayjs(getLocalDateTimeTZ(new Date(blockStartDate), getDeviceInfo().timezone)).format('MM/DD/YYYY h:mmA')}
      </span>
    </p>
    {['BARRED', 'EXTERNAL'].includes(blockType) && (
      <p className="block-reason qa-player-card-rewards-card-blocked-reason">Reason: {blockComment}</p>
    )}
    <hr />
  </div>
);

const RewardsModal = styled.div`
  width: 100%;

  p {
    align-items: center;
    display: flex;
    justify-content: space-between;
    padding: 0.1rem 0;
    &.centered {
      justify-content: center;
    }
    span {
      &:nth-child(1) {
        display: flex;
        font-weight: 600;
        justify-content: flex-start;
        text-align: left;
      }
      &:nth-child(2) {
        display: flex;
        justify-content: flex-end;
        text-align: right;
      }
    }
    &.identification {
      span {
        &:nth-child(1) {
          width: 50%;
        }
        &:nth-child(2) {
          width: 25%;
        }
        &:nth-child(3) {
          width: 10%;
        }
        &:nth-child(4) {
          text-align: right;
          width: 15%;
        }
      }
    }
  }

  hr {
    border: 0;
    height: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);

    &.double {
      border-top: medium double rgba(0, 0, 0, 0.1);
    }
  }
`;

const Activity = styled(Text)`
  background-color: ${({ theme }) => theme.colors.status.error};
  border-radius: ${({ theme }) => theme.borderRadius.xxlarge};
  color: ${({ theme }) => theme.colors.white};
  display: inline-block;
  margin-left: 1rem;
  padding: 0.6rem 1.5rem;
  text-transform: uppercase;
`;

const CloseButton = styled(Button)`
  margin: 0 auto;
`;
